import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from 'react'
import * as fcl from '@blocto/fcl'
import { GET_UPGRADE_PATHS } from 'flow/scripts/getUpgradePaths.script'
import { GET_UPGRADE_REGISTRY_DETAILS } from 'flow/scripts/getUpgradeRegistryDetails.script'
import { GET_UPGRADE_REQUIREMENTS } from 'flow/scripts/getUpgradeRequirements.script'

// Define the UpgradeRequirements type
type UpgradeRequirements = {
  quantity: string
  templateIds: [string]
}[]

interface UpgradeContextType {
  upgradePaths: { [key: string]: string }
  upgradeRegistryDetails: any
  upgradeRequirements: { [templateId: string]: UpgradeRequirements }
  isLoadingPaths: boolean
  isLoadingRegistry: boolean
  isLoadingRequirements: boolean
  getUpgradeRequirements: (templateId: string) => Promise<UpgradeRequirements>
  updateRegistryForUser: (
    templateId: string,
    userAddress: string,
    quantity: number
  ) => void
}

const UpgradeContext = createContext<UpgradeContextType>({
  upgradePaths: {},
  upgradeRegistryDetails: undefined,
  upgradeRequirements: {},
  isLoadingPaths: false,
  isLoadingRegistry: false,
  isLoadingRequirements: false,
  getUpgradeRequirements: async () => [],
  updateRegistryForUser: (
    templateId: string,
    userAddress: string,
    quantity: number
  ) => {
    /* no-op default */
  }
})

export const useUpgradeContext = () => useContext(UpgradeContext)

export const UpgradeProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [upgradePaths, setUpgradePaths] = useState<{ [key: string]: string }>(
    {}
  )
  const [upgradeRegistryDetails, setUpgradeRegistryDetails] =
    useState<any>(undefined)
  const [upgradeRequirements, setUpgradeRequirements] = useState<{
    [templateId: string]: UpgradeRequirements
  }>({})
  const [isLoadingPaths, setIsLoadingPaths] = useState(false)
  const [isLoadingRegistry, setIsLoadingRegistry] = useState(false)
  const [isLoadingRequirements, setIsLoadingRequirements] = useState(false)

  const getUpgradePaths = useCallback(async () => {
    if (isLoadingPaths || Object.keys(upgradePaths).length > 0) return

    setIsLoadingPaths(true)
    try {
      const response = await fcl.query({
        cadence: GET_UPGRADE_PATHS
      })
      setUpgradePaths(response)
    } catch (error) {
      console.error('Error fetching upgrade paths:', error)
    } finally {
      setIsLoadingPaths(false)
    }
  }, [isLoadingPaths, upgradePaths])

  const getUpgradeRegistryDetails = useCallback(async () => {
    if (isLoadingRegistry || upgradeRegistryDetails) return

    setIsLoadingRegistry(true)
    try {
      const response = await fcl.query({
        cadence: GET_UPGRADE_REGISTRY_DETAILS
      })
      setUpgradeRegistryDetails(response)
    } catch (error) {
      console.error('Error fetching upgrade registry details:', error)
    } finally {
      setIsLoadingRegistry(false)
    }
  }, [isLoadingRegistry, upgradeRegistryDetails])

  const getUpgradeRequirements = useCallback(
    async function getUpgradeRequirements(
      templateId: string
    ): Promise<UpgradeRequirements> {
      if (upgradeRequirements[templateId]) {
        return upgradeRequirements[templateId]
      }

      if (isLoadingRequirements) return []

      setIsLoadingRequirements(true)
      try {
        const response = await fcl.query({
          cadence: GET_UPGRADE_REQUIREMENTS,
          args: (arg: any, t: any) => [arg(templateId, fcl.t.UInt64)]
        })
        const requirements = response as UpgradeRequirements
        setUpgradeRequirements((prev) => ({
          ...prev,
          [templateId]: requirements
        }))
        return requirements
      } catch (error) {
        console.error('Error fetching upgrade requirements:', error)
        return []
      } finally {
        setIsLoadingRequirements(false)
      }
    },
    [isLoadingRequirements, upgradeRequirements]
  )

  const updateRegistryForUser = useCallback(
    (templateId: string, userAddress: string, quantity: number) => {
      setUpgradeRegistryDetails((prev: any) => {
        if (!prev) {
          // If no registry exists yet, create it
          const newRegistry = {
            [templateId]: Array(quantity).fill(userAddress)
          }
          return newRegistry
        }

        const currentAddresses = prev[templateId] || []
        const newAddresses = [...currentAddresses]

        // Add the user's address the specified number of times
        for (let i = 0; i < quantity; i++) {
          newAddresses.push(userAddress)
        }

        const updatedRegistry = {
          ...prev,
          [templateId]: newAddresses
        }
        return updatedRegistry
      })
    },
    []
  )

  useEffect(() => {
    getUpgradePaths()
    getUpgradeRegistryDetails()
  }, [getUpgradePaths, getUpgradeRegistryDetails])

  return (
    <UpgradeContext.Provider
      value={{
        upgradePaths,
        upgradeRegistryDetails,
        upgradeRequirements,
        isLoadingPaths,
        isLoadingRegistry,
        isLoadingRequirements,
        getUpgradeRequirements,
        updateRegistryForUser
      }}
    >
      {children}
    </UpgradeContext.Provider>
  )
}
